import { DriveSourceTargetEventType } from 'contexts/WebSocketContext';
import { List } from 'immutable';
import { useState } from 'react';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { ProjectDriveSourceTarget } from 'records/ProjectDriveSourceTarget';
import { merge, Observable } from 'rxjs';
import { scan } from 'rxjs/operators';
import FolderTrie from 'utils/FolderTrie';
import useDriveSourceTargetEvents from './useDriveSourceTargetEvents';

export type ClientActionEventMessage =
  | {
      event: 'initial-drive-source-targets';
      targets: List<ProjectDriveSourceTarget>;
    }
  | {
      event: 'initial-integration-targets';
      targets: List<IntegrationsSelectedItem>;
    }
  | {
      event: 'drive-source-targets-added';
      targets: List<IntegrationsSelectedItem>;
    }
  | { event: 'drive-source-targets-removed'; ids: List<string> }
  | {
      event: 'drive-source-single-target-set';
      target: IntegrationsSelectedItem;
    };

const prependFolderPathId = (folderPathIds: Array<string>, siteId?: string) =>
  siteId ? [siteId, ...folderPathIds] : folderPathIds;

const useFileBrowserFolderTrie = (
  userActionsObservable: Observable<ClientActionEventMessage>,
  connectionId: string,
  providerType: string,
  projectDriveIdForEvents?: string
): Observable<FolderTrie<IntegrationsSelectedItem>> => {
  const driveSourceTargetEvents = useDriveSourceTargetEvents(
    projectDriveIdForEvents
  );

  const [trieObservable] = useState(
    (projectDriveIdForEvents
      ? merge(driveSourceTargetEvents, userActionsObservable)
      : userActionsObservable
    ).pipe(
      scan((trie, event) => {
        if (event.event === 'initial-drive-source-targets') {
          return new FolderTrie<IntegrationsSelectedItem>().insertAll(
            event.targets
              .map(({ folderPathIds, itemType, id, driveId, siteId }) => ({
                folderPathIds: prependFolderPathId(
                  folderPathIds.toArray(),
                  siteId
                ),
                value: IntegrationsSelectedItem.deserialize({
                  id,
                  itemType,
                  driveId,
                  folderPathIds: folderPathIds.toArray(),
                  siteId,
                  connectionId,
                  providerType,
                }),
              }))
              .toArray()
          );
        }

        if (event.event === 'initial-integration-targets') {
          return trie.insertAll(
            event.targets
              .map((item) => ({
                folderPathIds: prependFolderPathId(
                  item.folderPathIds.toArray(),
                  item.siteId
                ),
                value: item,
              }))
              .toArray()
          );
        }

        if (
          event.event === DriveSourceTargetEventType.Added &&
          event.payload.target.folderIdPath
        ) {
          return trie.insert(
            prependFolderPathId(
              event.payload.target.folderIdPath,
              event.payload.target.siteId
            ),
            IntegrationsSelectedItem.deserialize({
              id: event.payload.target.itemId,
              itemType: event.payload.target.type,
              driveId: event.payload.target.driveId,
              folderPathIds: event.payload.target.folderIdPath,
              siteId: event.payload.target.siteId,
              connectionId,
              providerType,
            })
          );
        }

        if (event.event === DriveSourceTargetEventType.Removed) {
          return trie.delete(event.payload.target.itemId);
        }

        if (event.event === 'drive-source-targets-added') {
          return trie.insertAll(
            event.targets
              .map((item) => ({
                folderPathIds: prependFolderPathId(
                  item.folderPathIds.toArray(),
                  item.siteId
                ),
                value: item,
              }))
              .toArray()
          );
        }

        if (event.event === 'drive-source-targets-removed') {
          return trie.deleteAll(event.ids.toArray());
        }

        if (
          event.event === 'drive-source-single-target-set' &&
          event.target.folderPathIds
        ) {
          return new FolderTrie<IntegrationsSelectedItem>().insert(
            prependFolderPathId(
              event.target.folderPathIds.toArray(),
              event.target.siteId
            ),
            event.target
          );
        }

        return trie;
      }, new FolderTrie<IntegrationsSelectedItem>())
    )
  );

  return trieObservable;
};

export default useFileBrowserFolderTrie;
